import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePreferences } from '../App';
import { useData } from '../context/DataContext';
import { ArrowLeft, Flame, TrendingDown, CheckCircle2, XCircle, AlertTriangle, Trophy, Sun, Moon, TrendingUp } from 'lucide-react';
import { HabitType, LogStatus, PrayerQuality } from '../../types';
import AnnualCalendar from '../components/AnnualCalendar';
import PrayerInsightCard from '../components/PrayerInsightCard';
import HabitSettingsSection from '../components/HabitSettingsSection';
import GeneralTrendChart from '../components/GeneralTrendChart';
import CounterTrendChart from '../components/CounterTrendChart';
import { ICON_MAP, IconName } from '../utils/iconMap';
import { INITIAL_HABITS } from '../../constants';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useTheme } from '../hooks/useTheme';
import { resolveHabitColor } from '../lib/brand';

const HabitDetails: React.FC = () => {
  const { habitId } = useParams<{ habitId: string }>();
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const { habits, logs } = useData();
  const { palette } = useTheme();

  const habit = habits.find(h => h.id === habitId);

  // Check if this is a Rawatib prayer
  const rawatibIds = [
    'fajr_sunnah',
    'dhuhr_sunnah_before_1',
    'dhuhr_sunnah_before_2',
    'dhuhr_sunnah_after',
    'maghrib_sunnah',
    'isha_sunnah',
  ];
  const isRawatib = habit?.presetId === 'rawatib' || (habit && rawatibIds.includes(habit.id));
  
  // Check if this is a twice-daily habit (COUNTER with target 2)
  const isTwiceDaily = habit?.type === HabitType.COUNTER && habit?.dailyTarget === 2;

  if (!habit) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">
            {preferences.language === 'ar' ? 'العادة غير موجودة' : 'Habit not found'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary text-on-fill rounded-lg"
          >
            {preferences.language === 'ar' ? 'العودة إلى الصفحة الرئيسية' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  const habitLogs = logs.filter(l => l.habitId === habitId);
  const displayName = preferences.language === 'ar' ? habit.nameAr : habit.name;
  const isArabic = preferences.language === 'ar';

  // Get habit color and icon
  const habitColor = resolveHabitColor(habit.color || INITIAL_HABITS.find(h => h.id === habit.id)?.color, palette);
  const HabitIcon = useMemo(() => {
    const iconName = habit.icon || INITIAL_HABITS.find(h => h.id === habit.id)?.icon;
    if (iconName && ICON_MAP[iconName as IconName]) {
      return ICON_MAP[iconName as IconName];
    }
    return ICON_MAP.Activity;
  }, [habit]);

  // Helper to parse YYYY-MM-DD strings locally without UTC shifting
  const parseLocalISO = (dateStr: string) => {
    if (!dateStr) return new Date();
    const parts = dateStr.split('-');
    if (parts.length !== 3) return new Date(dateStr);
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  };

  // Calculate metrics for Rawatib (similar to regular habits but prayer-like)
  const rawatibMetrics = useMemo(() => {
    if (!isRawatib) return null;

    const total = habitLogs.length;
    const completed = habitLogs.filter(l => l.status === LogStatus.DONE).length;
    const failed = habitLogs.filter(l => l.status === LogStatus.FAIL).length;
    
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    // Calculate best streak (consecutive DONE days)
    const sortedLogs = [...habitLogs].sort((a, b) => a.date.localeCompare(b.date));
    let bestStreak = 0;
    let currentStreak = 0;
    let prevDate: Date | null = null;
    
    const doneLogs = sortedLogs.filter(l => l.status === LogStatus.DONE);
    doneLogs.forEach(log => {
      const logDate = parseLocalISO(log.date);
      if (prevDate) {
        const dayDiff = Math.round((logDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        if (dayDiff === 1) {
          currentStreak++;
        } else {
          currentStreak = 1;
        }
      } else {
        currentStreak = 1;
      }
      bestStreak = Math.max(bestStreak, currentStreak);
      prevDate = logDate;
    });
    
    // Top 3 obstacles
    const reasonCounts: Record<string, number> = {};
    habitLogs
      .filter(l => l.status !== LogStatus.DONE && l.reason && l.reason.trim())
      .forEach(l => {
        const reason = l.reason!.trim();
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });
    
    const topObstacles = Object.entries(reasonCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([reason, count]) => ({
        reason,
        count,
        pct: total > 0 ? Math.round((count / total) * 100) : 0
      }));
    
    return {
      total,
      completed,
      failed,
      completionRate,
      bestStreak,
      topObstacles
    };
  }, [habitLogs, isRawatib]);

  // Calculate metrics for twice-daily habits with two-digit encoding
  // value = (MorningState * 10) + EveningState
  // State Key: 0 = Pending, 1 = Done, 2 = Fail
  const twiceDailyMetrics = useMemo(() => {
    if (!isTwiceDaily) return null;

    // Helper to decode two-digit value
    const decode = (value: number) => {
      const amState = Math.floor(value / 10);
      const pmState = value % 10;
      return { amState, pmState };
    };

    // Filter only completed logs (PM not pending)
    const completedLogs = habitLogs.filter(l => {
      const { pmState } = decode(l.value || 0);
      return pmState !== 0; // PM has been logged
    });

    const total = completedLogs.length;
    
    // Perfect days: Both AM and PM Done (value = 11)
    const perfectDays = completedLogs.filter(l => l.value === 11).length;
    
    // Partial days: One done, one fail (values: 12, 21)
    const partialDays = completedLogs.filter(l => {
      const { amState, pmState } = decode(l.value || 0);
      return (amState === 1 && pmState === 2) || (amState === 2 && pmState === 1);
    }).length;
    
    // Both failed (value = 22)
    const bothFailed = completedLogs.filter(l => l.value === 22).length;
    
    // Count individual session stats
    const amDone = completedLogs.filter(l => {
      const { amState } = decode(l.value || 0);
      return amState === 1;
    }).length;
    
    const pmDone = completedLogs.filter(l => {
      const { pmState } = decode(l.value || 0);
      return pmState === 1;
    }).length;
    
    const perfectRate = total > 0 ? Math.round((perfectDays / total) * 100) : 0;
    const amSuccessRate = total > 0 ? Math.round((amDone / total) * 100) : 0;
    const pmSuccessRate = total > 0 ? Math.round((pmDone / total) * 100) : 0;
    
    // Calculate best streak (consecutive perfect days - value = 11)
    const sortedLogs = [...completedLogs].sort((a, b) => a.date.localeCompare(b.date));
    let bestStreak = 0;
    let currentStreak = 0;
    let prevDate: Date | null = null;
    
    const perfectLogs = sortedLogs.filter(l => l.value === 11);
    perfectLogs.forEach(log => {
      const logDate = parseLocalISO(log.date);
      if (prevDate) {
        const dayDiff = Math.round((logDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        if (dayDiff === 1) {
          currentStreak++;
        } else {
          currentStreak = 1;
        }
      } else {
        currentStreak = 1;
      }
      bestStreak = Math.max(bestStreak, currentStreak);
      prevDate = logDate;
    });
    
    return {
      total,
      perfectDays,
      partialDays,
      bothFailed,
      amDone,
      pmDone,
      perfectRate,
      amSuccessRate,
      pmSuccessRate,
      bestStreak
    };
  }, [habitLogs, isTwiceDaily]);

  // Calculate metrics for prayers
  const prayerMetrics = useMemo(() => {
    if (habit.type !== HabitType.PRAYER || isRawatib) return null;

    const total = habitLogs.length;
    const missed = habitLogs.filter(l => l.value === PrayerQuality.MISSED).length;
    const onTime = habitLogs.filter(l => l.value === PrayerQuality.ON_TIME).length;
    const inGroup = habitLogs.filter(l => l.value === PrayerQuality.JAMAA).length;
    const takbirah = habitLogs.filter(l => l.value === PrayerQuality.TAKBIRAH).length;
    
    const takbirahRate = total > 0 ? Math.round((takbirah / total) * 100) : 0;
    
    // Calculate best streak (consecutive Takbirah days)
    const sortedLogs = [...habitLogs].sort((a, b) => a.date.localeCompare(b.date));
    let bestStreak = 0;
    let currentStreak = 0;
    let prevDate: Date | null = null;
    
    const takbirahLogs = sortedLogs.filter(l => l.value === PrayerQuality.TAKBIRAH);
    takbirahLogs.forEach(log => {
      const logDate = parseLocalISO(log.date);
      if (prevDate) {
        const dayDiff = Math.round((logDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        if (dayDiff === 1) {
          currentStreak++;
        } else {
          currentStreak = 1;
        }
      } else {
        currentStreak = 1;
      }
      bestStreak = Math.max(bestStreak, currentStreak);
      prevDate = logDate;
    });
    
    // Top 3 obstacles (reasons for non-Takbirah)
    const reasonCounts: Record<string, number> = {};
    habitLogs
      .filter(l => l.value !== undefined && l.value < PrayerQuality.TAKBIRAH && l.reason && l.reason.trim())
      .forEach(l => {
        const reason = l.reason!.trim();
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });
    
    const topObstacles = Object.entries(reasonCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([reason, count]) => ({
        reason,
        count,
        pct: habitLogs.length > 0 ? Math.round((count / habitLogs.length) * 100) : 0
      }));
    
    return {
      total,
      missed,
      onTime,
      inGroup,
      takbirah,
      takbirahRate,
      bestStreak,
      topObstacles
    };
  }, [habitLogs, habit.type]);

  // Rate color helper
  const getRateColorStyle = (percentage: number) => {
    if (percentage <= 25) return palette.missed.deep;
    if (percentage <= 50) return palette.missed.bright;
    if (percentage <= 75) return palette.onTime.bright;
    return palette.takbirah.bright;
  };

  // Calculate metrics for regular habits
  const metrics = useMemo(() => {
    const sortedLogs = [...habitLogs].sort((a, b) => a.date.localeCompare(b.date));
    
    // Count totals
    const totalDone = habitLogs.filter(l => l.status === LogStatus.DONE).length;
    const totalFails = habitLogs.filter(l => l.status === LogStatus.FAIL).length;
    
    // Calculate best streak (consecutive DONE days)
    let bestStreak = 0;
    let currentStreak = 0;
    let prevDate: Date | null = null;
    
    const doneLogs = sortedLogs.filter(l => l.status === LogStatus.DONE);
    doneLogs.forEach(log => {
      const logDate = parseLocalISO(log.date);
      if (prevDate) {
        const dayDiff = Math.round((logDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        if (dayDiff === 1) {
          currentStreak++;
        } else {
          currentStreak = 1;
        }
      } else {
        currentStreak = 1;
      }
      bestStreak = Math.max(bestStreak, currentStreak);
      prevDate = logDate;
    });
    
    // Calculate worst fail streak (consecutive FAIL days)
    let worstFailStreak = 0;
    let currentFailStreak = 0;
    prevDate = null;
    
    const failLogs = sortedLogs.filter(l => l.status === LogStatus.FAIL);
    failLogs.forEach(log => {
      const logDate = parseLocalISO(log.date);
      if (prevDate) {
        const dayDiff = Math.round((logDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        if (dayDiff === 1) {
          currentFailStreak++;
        } else {
          currentFailStreak = 1;
        }
      } else {
        currentFailStreak = 1;
      }
      worstFailStreak = Math.max(worstFailStreak, currentFailStreak);
      prevDate = logDate;
    });
    
    // Top 3 reasons for failures
    const reasonCounts: Record<string, number> = {};
    failLogs.forEach(log => {
      if (log.reason) {
        reasonCounts[log.reason] = (reasonCounts[log.reason] || 0) + 1;
      }
    });
    
    const topReasons = Object.entries(reasonCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([reason, count]) => ({ reason, count }));
    
    return {
      totalDone,
      totalFails,
      bestStreak,
      worstFailStreak,
      topReasons
    };
  }, [habitLogs]);

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-md border-b border-white/5 pt-[env(safe-area-inset-top)]">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-white truncate">{displayName}</h1>
            <p className="text-xs text-gray-400">
              {habit.type === HabitType.PRAYER 
                ? (preferences.language === 'ar' ? 'صلاة' : 'Prayer')
                : isTwiceDaily
                ? (preferences.language === 'ar' ? 'مرتين يومياً' : 'Twice Daily')
                : habit.type === HabitType.COUNTER
                ? (preferences.language === 'ar' ? 'عداد' : 'Counter')
                : (preferences.language === 'ar' ? 'عادة' : 'Habit')
              }
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {isTwiceDaily && twiceDailyMetrics ? (
          <>
            {/* Header with Split-Doughnut Icon */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-emerald-500/20 to-red-500/10 border border-emerald-500/20">
                <svg width="28" height="28" viewBox="0 0 28 28">
                  <circle cx="14" cy="14" r="10" fill="none" stroke={palette.track} strokeWidth="3" />
                  <path d="M 14 4 A 10 10 0 0 0 14 24" fill="none" stroke={palette.takbirah.bright} strokeWidth="3" strokeLinecap="round" />
                  <path d="M 14 4 A 10 10 0 0 1 14 24" fill="none" stroke={palette.takbirah.bright} strokeWidth="3" strokeLinecap="round" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-gray-500">
                  {isArabic ? 'عادة مرتين يومياً' : 'Dual-Session Habit'}
                </p>
              </div>
            </div>

            {/* Annual Calendar Section with Split-Doughnut */}
            <div className="bg-card rounded-xl border border-emerald-500/20 p-4">
              <h2 className="text-sm text-gray-400 font-semibold uppercase tracking-wide mb-4">
                {isArabic ? 'التقويم السنوي' : 'Annual Calendar'}
              </h2>
              <AnnualCalendar
                habitId={habit.id}
                habitType={habit.type}
                habitColor={habitColor}
                logs={logs}
                language={preferences.language}
                dailyTarget={habit.dailyTarget}
              />
            </div>

            {/* Twice-Daily Ring Visualization */}
            <div className="bg-card rounded-xl border border-emerald-500/30 bg-slate-900/80 shadow-lg overflow-hidden p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm text-gray-400 font-semibold uppercase tracking-wide">
                  {isArabic ? 'إحصائيات الجلسات' : 'Session Stats'}
                </h2>
                {twiceDailyMetrics.bestStreak > 0 && (
                  <div className="flex items-center gap-1 text-xs bg-orange-500/10 px-2 py-1 rounded-lg text-orange-400 border border-orange-500/20">
                    <Trophy size={12} />
                    <span className="font-bold">{twiceDailyMetrics.bestStreak}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-4" dir="ltr">
                {/* Ring Chart */}
                <div className="flex flex-col items-center justify-center">
                  <div className="relative h-24 w-24">
                    {(() => {
                      const chartData = [
                        { name: isArabic ? 'يوم مثالي' : 'Perfect', value: twiceDailyMetrics.perfectDays, color: palette.takbirah.bright },
                        { name: isArabic ? 'جزئي' : 'Partial', value: twiceDailyMetrics.partialDays, color: palette.onTime.bright },
                        { name: isArabic ? 'فشل' : 'Failed', value: twiceDailyMetrics.bothFailed, color: palette.missed.bright },
                      ].filter(d => d.value > 0);

                      const finalChartData = chartData.length > 0 ? chartData : [{ name: 'Empty', value: 1, color: palette.track }];
                      const rateColor = getRateColorStyle(twiceDailyMetrics.perfectRate);

                      return (
                        <>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={finalChartData} cx="50%" cy="50%" innerRadius={30} outerRadius={42} paddingAngle={2} dataKey="value" stroke="none">
                                {finalChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-lg font-bold" style={{ color: rateColor }}>{twiceDailyMetrics.perfectRate}%</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-xl font-bold" style={{ color: getRateColorStyle(twiceDailyMetrics.perfectRate) }}>{twiceDailyMetrics.perfectDays}</span>
                    <span className="text-xs text-gray-500">/ {twiceDailyMetrics.total}</span>
                  </div>
                  <span className="text-[10px] text-gray-400 mt-0.5">{isArabic ? 'أيام مثالية' : 'Perfect Days'}</span>
                </div>

                {/* Details */}
                <div className="flex-1 flex flex-col gap-3 min-w-0">
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                      {isArabic ? 'نتائج الأيام' : 'Day Results'}
                    </h4>
                    <div className="flex flex-col gap-1">
                      {[
                        { 
                          label: isArabic ? 'يوم مثالي' : 'Perfect Day', 
                          val: twiceDailyMetrics.perfectDays, 
                          pct: twiceDailyMetrics.perfectRate, 
                          color: palette.takbirah.bright,
                          icon: CheckCircle2 
                        },
                        { 
                          label: isArabic ? 'جزئي (1 تم / 1 فشل)' : 'Partial (1 Pass / 1 Fail)', 
                          val: twiceDailyMetrics.partialDays, 
                          pct: twiceDailyMetrics.total > 0 ? Math.round((twiceDailyMetrics.partialDays / twiceDailyMetrics.total) * 100) : 0, 
                          color: palette.onTime.bright,
                          icon: AlertTriangle 
                        },
                        { 
                          label: isArabic ? 'فشل كلي' : 'Both Failed', 
                          val: twiceDailyMetrics.bothFailed, 
                          pct: twiceDailyMetrics.total > 0 ? Math.round((twiceDailyMetrics.bothFailed / twiceDailyMetrics.total) * 100) : 0, 
                          color: palette.missed.bright,
                          icon: XCircle 
                        },
                      ].map(item => {
                        const Icon = item.icon;
                        return (
                          <div key={item.label} className="flex items-center gap-2 text-xs">
                            <Icon size={10} style={{ color: item.color }} className="shrink-0" />
                            <span className="text-gray-400 flex-1 truncate">{item.label}</span>
                            <span className="font-bold tabular-nums" style={{ color: item.color }}>{item.pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Session Success Rates */}
                  <div className="h-px bg-slate-700/50" />
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                      {isArabic ? 'نجاح الجلسات' : 'Session Success'}
                    </h4>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-xs">
                        <Sun size={10} className="text-amber-400 shrink-0" />
                        <span className="text-gray-400 flex-1">{isArabic ? 'نجاح الصباح' : 'AM Success'}</span>
                        <span className="font-bold tabular-nums text-amber-400">{twiceDailyMetrics.amSuccessRate}%</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Moon size={10} className="text-blue-400 shrink-0" />
                        <span className="text-gray-400 flex-1">{isArabic ? 'نجاح المساء' : 'PM Success'}</span>
                        <span className="font-bold tabular-nums text-blue-400">{twiceDailyMetrics.pmSuccessRate}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-4 gap-2">
              {/* Best Streak */}
              <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-800 text-center">
                <div className="flex items-center justify-center gap-1 text-orange-400 mb-1">
                  <Flame size={12} />
                </div>
                <div className="font-mono text-lg font-bold text-white">{twiceDailyMetrics.bestStreak}</div>
                <div className="text-[8px] text-gray-500 uppercase">{isArabic ? 'تتابع' : 'Streak'}</div>
              </div>
              
              {/* Perfect Days */}
              <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-800 text-center">
                <div className="flex items-center justify-center gap-1 text-emerald-400 mb-1">
                  <CheckCircle2 size={12} />
                </div>
                <div className="font-mono text-lg font-bold text-white">{twiceDailyMetrics.perfectDays}</div>
                <div className="text-[8px] text-gray-500 uppercase">{isArabic ? 'مثالي' : 'Perfect'}</div>
              </div>
              
              {/* Partial Days */}
              <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-800 text-center">
                <div className="flex items-center justify-center gap-1 text-amber-400 mb-1">
                  <AlertTriangle size={12} />
                </div>
                <div className="font-mono text-lg font-bold text-white">{twiceDailyMetrics.partialDays}</div>
                <div className="text-[8px] text-gray-500 uppercase">{isArabic ? 'جزئي' : 'Partial'}</div>
              </div>
              
              {/* Both Failed */}
              <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-800 text-center">
                <div className="flex items-center justify-center gap-1 text-red-400 mb-1">
                  <XCircle size={12} />
                </div>
                <div className="font-mono text-lg font-bold text-white">{twiceDailyMetrics.bothFailed}</div>
                <div className="text-[8px] text-gray-500 uppercase">{isArabic ? 'فشل' : 'Failed'}</div>
              </div>
            </div>

            {/* Trend Chart for Twice-Daily Habits */}
            <div className="bg-card rounded-xl border border-emerald-500/20 p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp size={12} className="text-gray-400" />
                <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                  {isArabic ? 'اتجاه الإنجاز' : 'Completion Trend'}
                </h4>
              </div>
              <CounterTrendChart
                logs={logs}
                habitId={habit.id}
                dailyTarget={habit.dailyTarget || 2}
                language={preferences.language}
              />
            </div>
          </>
        ) : habit.type === HabitType.REGULAR || habit.type === HabitType.COUNTER ? (
          <>
            {/* Header with Icon */}
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${habitColor}20` }}
              >
                <HabitIcon size={24} style={{ color: habitColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-gray-500">
                  {isArabic ? 'عرض سنوي' : 'Annual View'}
                </p>
              </div>
            </div>

            {/* Annual Calendar Section */}
            <div className="bg-card rounded-xl border border-slate-800 p-4">
              <h2 className="text-sm text-gray-400 font-semibold uppercase tracking-wide mb-4">
                {isArabic ? 'التقويم السنوي' : 'Annual Calendar'}
              </h2>
              <AnnualCalendar
                habitId={habit.id}
                habitType={habit.type}
                habitColor={habitColor}
                logs={logs}
                language={preferences.language}
                dailyTarget={habit.dailyTarget}
              />
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-4 gap-2">
              {/* Best Streak */}
              <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-800 text-center">
                <div className="flex items-center justify-center gap-1 text-orange-400 mb-1">
                  <Flame size={12} />
                </div>
                <div className="font-mono text-lg font-bold text-white">{metrics.bestStreak}</div>
                <div className="text-[8px] text-gray-500 uppercase">{isArabic ? 'أفضل تتابع' : 'Best'}</div>
              </div>
              
              {/* Worst Fail Streak */}
              <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-800 text-center">
                <div className="flex items-center justify-center gap-1 text-red-400 mb-1">
                  <TrendingDown size={12} />
                </div>
                <div className="font-mono text-lg font-bold text-white">{metrics.worstFailStreak}</div>
                <div className="text-[8px] text-gray-500 uppercase">{isArabic ? 'أسوأ فشل' : 'Worst'}</div>
              </div>
              
              {/* Total Done */}
              <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-800 text-center">
                <div className="flex items-center justify-center gap-1 text-emerald-400 mb-1">
                  <CheckCircle2 size={12} />
                </div>
                <div className="font-mono text-lg font-bold text-white">{metrics.totalDone}</div>
                <div className="text-[8px] text-gray-500 uppercase">{isArabic ? 'تم' : 'Done'}</div>
              </div>
              
              {/* Total Fails */}
              <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-800 text-center">
                <div className="flex items-center justify-center gap-1 text-red-400 mb-1">
                  <XCircle size={12} />
                </div>
                <div className="font-mono text-lg font-bold text-white">{metrics.totalFails}</div>
                <div className="text-[8px] text-gray-500 uppercase">{isArabic ? 'فشل' : 'Fails'}</div>
              </div>
            </div>

            {/* Top Reasons for Failures */}
            {metrics.topReasons.length > 0 && (
              <div className="bg-slate-900/30 rounded-xl p-3 border border-slate-800">
                <div className="flex items-center gap-1.5 text-[10px] text-gray-400 mb-2">
                  <AlertTriangle size={10} />
                  <span className="uppercase font-semibold tracking-wider">
                    {isArabic ? 'أسباب الفشل الشائعة' : 'Top Fail Reasons'}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {metrics.topReasons.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="text-gray-300 truncate flex-1">{item.reason}</span>
                      <span className="text-gray-500 font-mono ml-2">{item.count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trend Chart for Regular/Counter Habits */}
            <div className="bg-card rounded-xl border border-slate-800 p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp size={12} className="text-gray-400" />
                <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                  {habit.type === HabitType.COUNTER 
                    ? (isArabic ? 'اتجاه القيمة' : 'Value Trend')
                    : (isArabic ? 'اتجاه النجاح' : 'Success Trend')}
                </h4>
              </div>
              {habit.type === HabitType.COUNTER && !isTwiceDaily ? (
                <CounterTrendChart
                  logs={logs}
                  habitId={habit.id}
                  dailyTarget={habit.dailyTarget || 1}
                  language={preferences.language}
                />
              ) : (
                <GeneralTrendChart
                  logs={logs}
                  habitId={habit.id}
                  language={preferences.language}
                />
              )}
            </div>
          </>
        ) : habit.type === HabitType.PRAYER && prayerMetrics ? (
          <>
            {/* Annual Calendar Section for Prayer */}
            <div className="bg-card rounded-xl border border-slate-800 p-4">
              <h2 className="text-sm text-gray-400 font-semibold uppercase tracking-wide mb-4">
                {isArabic ? 'التقويم السنوي' : 'Annual Calendar'}
              </h2>
              <AnnualCalendar
                habitId={habit.id}
                habitType={habit.type}
                habitColor={habitColor}
                logs={logs}
                language={preferences.language}
              />
            </div>

            {/* Ring Visualization */}
            <div className="bg-card rounded-xl border border-primary/30 bg-slate-900/80 shadow-lg overflow-hidden p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm text-gray-400 font-semibold uppercase tracking-wide">
                  {isArabic ? 'توزيع الجودة' : 'Quality Breakdown'}
                </h2>
                {prayerMetrics.bestStreak > 0 && (
                  <div className="flex items-center gap-1 text-xs bg-orange-500/10 px-2 py-1 rounded-lg text-orange-400 border border-orange-500/20">
                    <Trophy size={12} />
                    <span className="font-bold">{prayerMetrics.bestStreak}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-4" dir="ltr">
                {/* Ring Chart */}
                <div className="flex flex-col items-center justify-center">
                  <div className="relative h-24 w-24">
                    {(() => {
                      const chartData = [
                        { name: isArabic ? 'تكبيرة الإحرام' : 'Takbirah', value: prayerMetrics.takbirah, color: palette.takbirah.bright },
                        { name: isArabic ? 'جماعة' : 'In Group', value: prayerMetrics.inGroup, color: palette.jamaa.bright },
                        { name: isArabic ? 'في الوقت' : 'On Time', value: prayerMetrics.onTime, color: palette.onTime.bright },
                        { name: isArabic ? 'فائتة' : 'Missed', value: prayerMetrics.missed, color: palette.missed.bright },
                      ].filter(d => d.value > 0);

                      const finalChartData = chartData.length > 0 ? chartData : [{ name: 'Empty', value: 1, color: palette.track }];
                      const rateColor = getRateColorStyle(prayerMetrics.takbirahRate);

                      return (
                        <>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={finalChartData} cx="50%" cy="50%" innerRadius={30} outerRadius={42} paddingAngle={2} dataKey="value" stroke="none">
                                {finalChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-lg font-bold" style={{ color: rateColor }}>{prayerMetrics.takbirahRate}%</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-xl font-bold" style={{ color: getRateColorStyle(prayerMetrics.takbirahRate) }}>{prayerMetrics.takbirah}</span>
                    <span className="text-xs text-gray-500">/ {prayerMetrics.total}</span>
                  </div>
                  <span className="text-[10px] text-gray-400 mt-0.5">{isArabic ? 'معدل التكبيرة' : 'Takbirah Rate'}</span>
                </div>

                {/* Quality Breakdown */}
                <div className="flex-1 flex flex-col gap-3 min-w-0">
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                      {isArabic ? 'التفاصيل' : 'Details'}
                    </h4>
                    <div className="flex flex-col gap-1">
                      {[
                        { label: isArabic ? 'تكبيرة الإحرام' : 'Takbirah', val: prayerMetrics.takbirah, pct: prayerMetrics.total > 0 ? Math.round((prayerMetrics.takbirah / prayerMetrics.total) * 100) : 0, color: palette.takbirah.bright },
                        { label: isArabic ? 'جماعة' : 'In Group', val: prayerMetrics.inGroup, pct: prayerMetrics.total > 0 ? Math.round((prayerMetrics.inGroup / prayerMetrics.total) * 100) : 0, color: palette.jamaa.bright },
                        { label: isArabic ? 'في الوقت' : 'On Time', val: prayerMetrics.onTime, pct: prayerMetrics.total > 0 ? Math.round((prayerMetrics.onTime / prayerMetrics.total) * 100) : 0, color: palette.onTime.bright },
                        { label: isArabic ? 'فائتة' : 'Missed', val: prayerMetrics.missed, pct: prayerMetrics.total > 0 ? Math.round((prayerMetrics.missed / prayerMetrics.total) * 100) : 0, color: palette.missed.bright },
                      ].map(item => (
                        <div key={item.label} className="flex items-center gap-2 text-xs">
                          <span 
                            className="w-2 h-2 rounded-full shrink-0" 
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-gray-400 flex-1">{item.label}</span>
                          <span className="font-bold tabular-nums" style={{ color: item.color }}>{item.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  {prayerMetrics.topObstacles.length > 0 && (
                    <div className="h-px bg-slate-700/50" />
                  )}

                  {/* Top Obstacles */}
                  {prayerMetrics.topObstacles.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-1">
                        <AlertTriangle size={10} className="text-amber-500" />
                        <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                          {isArabic ? 'أهم العوائق' : 'Top Obstacles'}
                        </h4>
                      </div>
                      <div className="space-y-1">
                        {prayerMetrics.topObstacles.map((obs, idx) => (
                          <div key={obs.reason} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-600 text-[10px] font-mono">{idx + 1}.</span>
                            <span className="text-gray-300 truncate flex-1">{obs.reason}</span>
                            <span className="text-amber-500/80 font-semibold text-[10px]">{obs.pct}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* AI Insight Section */}
            <PrayerInsightCard
              prayerId={habit.id}
              prayerName={displayName}
              logs={habitLogs}
              language={preferences.language}
            />
          </>
        ) : isRawatib && rawatibMetrics ? (
          <>
            {/* Annual Calendar Section for Rawatib */}
            <div className="bg-card rounded-xl border border-slate-800 p-4">
              <h2 className="text-sm text-gray-400 font-semibold uppercase tracking-wide mb-4">
                {isArabic ? 'التقويم السنوي' : 'Annual Calendar'}
              </h2>
              <AnnualCalendar
                habitId={habit.id}
                habitType={HabitType.REGULAR}
                habitColor={habitColor}
                logs={logs}
                language={preferences.language}
              />
            </div>

            {/* Ring Visualization for Rawatib */}
            <div className="bg-card rounded-xl border border-purple-500/30 bg-slate-900/80 shadow-lg overflow-hidden p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm text-gray-400 font-semibold uppercase tracking-wide">
                  {isArabic ? 'معدل الإكمال' : 'Completion Rate'}
                </h2>
                {rawatibMetrics.bestStreak > 0 && (
                  <div className="flex items-center gap-1 text-xs bg-orange-500/10 px-2 py-1 rounded-lg text-orange-400 border border-orange-500/20">
                    <Trophy size={12} />
                    <span className="font-bold">{rawatibMetrics.bestStreak}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-4" dir="ltr">
                {/* Ring Chart */}
                <div className="flex flex-col items-center justify-center">
                  <div className="relative h-24 w-24">
                    {(() => {
                      const chartData = [
                        { name: isArabic ? 'مكتمل' : 'Completed', value: rawatibMetrics.completed, color: palette.takbirah.bright },
                        { name: isArabic ? 'فشل' : 'Failed', value: rawatibMetrics.failed, color: palette.missed.bright },
                      ].filter(d => d.value > 0);

                      const finalChartData = chartData.length > 0 ? chartData : [{ name: 'Empty', value: 1, color: palette.track }];
                      const rateColor = getRateColorStyle(rawatibMetrics.completionRate);

                      return (
                        <>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={finalChartData} cx="50%" cy="50%" innerRadius={30} outerRadius={42} paddingAngle={2} dataKey="value" stroke="none">
                                {finalChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-lg font-bold" style={{ color: rateColor }}>{rawatibMetrics.completionRate}%</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-xl font-bold" style={{ color: getRateColorStyle(rawatibMetrics.completionRate) }}>{rawatibMetrics.completed}</span>
                    <span className="text-xs text-gray-500">/ {rawatibMetrics.total}</span>
                  </div>
                  <span className="text-[10px] text-gray-400 mt-0.5">{isArabic ? 'مكتمل' : 'Completed'}</span>
                </div>

                {/* Details */}
                <div className="flex-1 flex flex-col gap-3 min-w-0">
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                      {isArabic ? 'التفاصيل' : 'Details'}
                    </h4>
                    <div className="flex flex-col gap-1">
                      {[
                        { label: isArabic ? 'مكتمل' : 'Completed', val: rawatibMetrics.completed, pct: rawatibMetrics.completionRate, color: palette.takbirah.bright },
                        { label: isArabic ? 'فشل' : 'Failed', val: rawatibMetrics.failed, pct: 100 - rawatibMetrics.completionRate, color: palette.missed.bright },
                      ].map(item => (
                        <div key={item.label} className="flex items-center gap-2 text-xs">
                          <span 
                            className="w-2 h-2 rounded-full shrink-0" 
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-gray-400 flex-1">{item.label}</span>
                          <span className="font-bold tabular-nums" style={{ color: item.color }}>{item.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  {rawatibMetrics.topObstacles.length > 0 && (
                    <div className="h-px bg-slate-700/50" />
                  )}

                  {/* Top Obstacles */}
                  {rawatibMetrics.topObstacles.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-1">
                        <AlertTriangle size={10} className="text-amber-500" />
                        <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                          {isArabic ? 'أهم العوائق' : 'Top Obstacles'}
                        </h4>
                      </div>
                      <div className="space-y-1">
                        {rawatibMetrics.topObstacles.map((obs, idx) => (
                          <div key={obs.reason} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-600 text-[10px] font-mono">{idx + 1}.</span>
                            <span className="text-gray-300 truncate flex-1">{obs.reason}</span>
                            <span className="text-amber-500/80 font-semibold text-[10px]">{obs.pct}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Trend Chart for Rawatib */}
            <div className="bg-card rounded-xl border border-purple-500/20 p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp size={12} className="text-gray-400" />
                <h4 className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                  {isArabic ? 'اتجاه النجاح' : 'Success Trend'}
                </h4>
              </div>
              <GeneralTrendChart
                logs={logs}
                habitId={habit.id}
                language={preferences.language}
              />
            </div>

            {/* AI Insight Section for Rawatib */}
            <PrayerInsightCard
              prayerId={habit.id}
              prayerName={displayName}
              logs={habitLogs}
              language={preferences.language}
            />
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {isArabic ? 'نوع العادة غير مدعوم' : 'Unsupported habit type'}
            </p>
          </div>
        )}

        {/* Settings Section */}
        <div className="bg-card rounded-xl border border-slate-800 p-4">
          <HabitSettingsSection
            habit={habit}
            language={preferences.language}
            onClose={() => navigate('/')}
          />
        </div>
      </div>

      {/* Floating Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-slate-900/80 backdrop-blur-md border-t border-white/5 pb-[env(safe-area-inset-bottom)]">
        <div className="px-4 py-3 flex items-center justify-center">
          <button
            onClick={() => navigate('/')}
            className="w-full max-w-md py-3 rounded-lg bg-primary text-on-fill font-medium text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
          >
            <ArrowLeft size={18} />
            {isArabic ? 'العودة إلى الصفحة الرئيسية' : 'Back to Home'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HabitDetails;
